import { useEffect, useState } from "react";
import { formatMoney, parseAmountToCents, type Currency, type CustomVariableDef } from "@talentkontor/domain";
import { nameIn, t } from "../../../lib/sprache";

/** Eingabefeld je Typ einer eigenen Angabe (Text, Auswahl, mehrzeilig, Betrag, Datum, Zahl, Prozent, Ja/Nein). */
export function WertFeld({ def, value, onChange, currency, disabled, hint }: {
  def: CustomVariableDef;
  value: unknown;
  onChange: (v: unknown) => void;
  currency: Currency;
  disabled?: boolean;
  hint?: string;
}) {
  const id = `wert-${def.key}`;
  const label = `${nameIn(def.label.de, def.label.en)}${def.required ? "" : " (optional)"}`;
  const [moneyText, setMoneyText] = useState(typeof value === "number" ? formatMoney(value, currency, "de-DE").replace(/\s?[€A-Z]{1,3}$/u, "").trim() : "");
  const [moneyError, setMoneyError] = useState<string | null>(null);
  useEffect(() => {
    if (def.type === "money" && typeof value === "number") setMoneyText(formatMoney(value, currency, "de-DE").replace(/\s?[€A-Z]{1,3}$/u, "").trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [def.type]);
  const small = hint ? <small>{hint}</small> : null;

  if (def.type === "boolean") {
    return (
      <label className="row">
        <input id={id} type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} disabled={disabled} />
        {nameIn(def.label.de, def.label.en)}
        {small}
      </label>
    );
  }
  if (def.type === "lines") {
    return (
      <label htmlFor={id}>
        {label}
        <textarea id={id} value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value)} rows={3} disabled={disabled} />
        {small}
      </label>
    );
  }
  if (def.type === "money") {
    return (
      <label htmlFor={id}>
        {label} ({currency})
        <input id={id} inputMode="decimal" value={moneyText} disabled={disabled} aria-invalid={moneyError ? true : undefined}
          onChange={(e) => {
            setMoneyText(e.target.value);
            if (!e.target.value.trim()) { setMoneyError(null); onChange(undefined); return; }
            try { onChange(parseAmountToCents(e.target.value)); setMoneyError(null); } catch { setMoneyError(t("Bitte einen Betrag wie 1.500,00 eingeben.", "Please enter an amount such as 1,500.00.")); }
          }} />
        {moneyError ? <small className="tk-error">{moneyError}</small> : small}
      </label>
    );
  }
  if (def.type === "date") {
    return (
      <label htmlFor={id}>
        {label}
        <input id={id} type="date" value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value || undefined)} disabled={disabled} />
        {small}
      </label>
    );
  }
  if (def.type === "number" || def.type === "percent") {
    return (
      <label htmlFor={id}>
        {label}{def.type === "percent" ? " (%)" : ""}
        <input id={id} type="number" inputMode="decimal" step="any" value={typeof value === "number" ? String(value) : ""} disabled={disabled}
          onChange={(e) => onChange(e.target.value === "" ? undefined : Number(e.target.value))} />
        {small}
      </label>
    );
  }
  if (def.options?.length) {
    return (
      <label htmlFor={id}>
        {label}
        <select id={id} value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value || undefined)} disabled={disabled}>
          <option value="">{t("Bitte wählen", "Please choose")}</option>
          {def.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        {small}
      </label>
    );
  }
  return (
    <label htmlFor={id}>
      {label}
      <input id={id} value={typeof value === "string" ? value : value == null ? "" : String(value)} onChange={(e) => onChange(e.target.value)} disabled={disabled} />
      {small}
    </label>
  );
}
